import React, { useEffect, useMemo, useState } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import ConfirmModal from 'common/Modal/ConfirmModal';
import { WidgetGrouping, WidgetGroupingCategory as WidgetGroupingCategoryType } from 'common/Widgets/types';
import Button from 'components/lib/Button';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useCommunityViewWidgetModalContext } from 'components/View/create/CommunityViewWidgetModalContext';
import {
  convertAreaByUnitSystem,
  convertAreaToBaseUnitSystem,
  getAreaUnitFullLabel,
  getAreaUnitLabel,
  isMetricSystem,
} from 'utils/helpers/units';

import WidgetGroupingCategory from './WidgetGroupingCategory';

interface Props {
  open: boolean;
  groupBy: WidgetGrouping;
  onClose: any;
}

const Container = styled.div`
  display: flex;
  flex-flow: column;
`;

const Description = styled.div`
  margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row-reverse;
`;

const StyledButton = styled(Button)`
  border-radius: 15px;
  padding: 7px 22px 7px 10px;
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 10px;
  height: 30px;
  margin-right: 24px;
`;

const ButtonLabel = styled.div`
  font-family: var(--font-regular);
  font-weight: 500;
  font-size: 12px;
  height: 16px;
`;

const roundAreaValue = (value: number) => Math.round(value);

const WidgetGroupingCategoriesModal = ({ open, groupBy, onClose }: Props) => {
  const [form] = Form.useForm();
  const { community } = useCommunityViewContext();
  const { setGroupBy } = useCommunityViewWidgetModalContext();
  const shouldConvertArea = isMetricSystem(community);

  const initialCategories = useMemo(
    () =>
      groupBy?.categories?.map((category) => ({
        range_start: category.range_start,
        range_end: category.range_end,
      })) || [],
    [groupBy?.categories]
  );

  const initialDisplayCategories = useMemo(() => {
    if (!shouldConvertArea) {
      return initialCategories;
    }

    return initialCategories.reduce<WidgetGroupingCategoryType[]>((categories, category, index) => {
      const convertedStart = roundAreaValue(convertAreaByUnitSystem(category.range_start, community));
      const convertedEnd = roundAreaValue(convertAreaByUnitSystem(category.range_end, community));
      const previousDisplayEnd = categories[index - 1]?.range_end;
      const displayStart = index === 0 ? convertedStart : previousDisplayEnd + 1;
      const displayEnd = Math.max(displayStart, convertedEnd);

      categories.push({
        range_start: displayStart,
        range_end: displayEnd,
      });

      return categories;
    }, []);
  }, [community, initialCategories, shouldConvertArea]);

  const getBaseCategory = (category: WidgetGroupingCategoryType, index: number) => {
    if (!shouldConvertArea) {
      return category;
    }

    const initialDisplayCategory = initialDisplayCategories[index];
    const initialCategory = initialCategories[index];

    if (
      initialDisplayCategory &&
      initialCategory &&
      category.range_start === initialDisplayCategory.range_start &&
      category.range_end === initialDisplayCategory.range_end
    ) {
      return initialCategory;
    }

    return {
      range_start: roundAreaValue(convertAreaToBaseUnitSystem(category.range_start, community)),
      range_end: roundAreaValue(convertAreaToBaseUnitSystem(category.range_end, community)),
    };
  };

  const handleConfirm = async (values: { categories: WidgetGroupingCategoryType[] }) => {
    setGroupBy({
      type: groupBy.type,
      categories: values.categories.map(getBaseCategory),
    });
    onClose();
  };

  const handleClose = () => {
    onClose();
    form.resetFields();
  };

  const initialValues = {
    categories: initialDisplayCategories,
  };

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [form, initialDisplayCategories, open]);

  return (
    <ConfirmModal
      open={open}
      disabled={false}
      title="Advanced Grouping"
      description={`By room ${getAreaUnitLabel(community)}`}
      confirmLabel="Apply"
      onConfirm={() => form.submit()}
      onCancel={handleClose}
    >
      <Container>
        <Description>
          Choose how to group by room {getAreaUnitFullLabel(community).toLowerCase()}. Every item will appear as a group
          on the graph. Each group has to start with a value equal or higher than the previous one.
        </Description>
        <Form form={form} initialValues={initialValues} onFinish={handleConfirm}>
          <Form.List name="categories" initialValue={initialValues?.categories || []}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ name }, index) => (
                  <WidgetGroupingCategory key={name} index={index} name={name} removeCategory={() => remove(name)} />
                ))}
                <ButtonContainer>
                  <StyledButton onClick={() => add()}>
                    <img src="/icons/add-blue.svg" height={16} width={16} alt="add" />
                    <ButtonLabel>Add Group</ButtonLabel>
                  </StyledButton>
                </ButtonContainer>
              </>
            )}
          </Form.List>
        </Form>
      </Container>
    </ConfirmModal>
  );
};

export default WidgetGroupingCategoriesModal;
